import axios from 'axios';
import { env } from 'env';

import { setCurrentWorldId } from '@helpers/currentWorld';

import { ICognitoParams, envCognitoParams } from './configure';

/**
 * Resolves which Cognito application this host should authenticate against,
 * before Amplify is configured, and records which project the host serves.
 *
 * Deliberately calls the endpoint directly rather than through WorldsService:
 * this runs at bootstrap, ahead of the module graph that reads auth state, and
 * must not pull those imports in.
 *
 * Any failure resolves to the `env.js` pool. A tenant that has not been given
 * its own pool, an offline API, or a malformed response must still produce a
 * working login screen rather than a blank page.
 *
 * @returns The pool to configure Amplify with; never rejects.
 */
export const resolveCognitoParams = async (): Promise<ICognitoParams> => {
  const { REACT_APP_API_URL, REACT_APP_APP_THEME_HOST_NAME } = env;

  const isLocalhost = ['localhost', '127.0.0.1'].includes(
    window.location.hostname,
  );

  const hostname =
    isLocalhost && REACT_APP_APP_THEME_HOST_NAME
      ? (REACT_APP_APP_THEME_HOST_NAME as string)
      : window.location.hostname;

  try {
    const { data } = await axios.get(
      `${REACT_APP_API_URL}api/v2/worlds/settings`,
      { params: { domainName: hostname }, timeout: 8000 },
    );

    // The same response carries the project the domain maps to. Recording it
    // here — `index.tsx` awaits this call before the first render — means every
    // later consumer, `GET /api/v1/profile/events` included, already has it.
    setCurrentWorldId(data?.worldId);

    const cognito = data?.settings?.cognito;

    if (
      cognito?.userPoolId &&
      cognito?.userPoolClientId &&
      cognito?.userPoolDomain
    ) {
      return {
        userPoolId: cognito.userPoolId,
        userPoolClientId: cognito.userPoolClientId,
        userPoolDomain: cognito.userPoolDomain,
      };
    }
  } catch (error) {
    console.error('Failed to resolve tenant Cognito params', error);
  }

  return envCognitoParams;
};
